import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Trash2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from '@/contexts/TranslationContext';

const DeleteAccountButton = () => {
  const { deleteAccount, user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [confirmText, setConfirmText] = useState('');
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const handleDeleteAccount = async () => {
    const trimmedText = confirmText.trim().toUpperCase();
    
    if (trimmedText !== 'ELIMINA') {
      toast.error('Scrivi "ELIMINA" per confermare');
      return;
    }

    setLoading(true);
    
    try {
      console.log('Starting account deletion process...');
      const { error } = await deleteAccount();
      
      if (!error) {
        console.log('Account deletion successful, redirecting...');
        setIsOpen(false);
        setConfirmText('');
        // Redirect will happen after signOut in useAuth
        setTimeout(() => {
          navigate('/account-deleted');
        }, 500);
      } else {
        console.error('Account deletion failed:', error);
        toast.error(error.message || 'Errore durante l\'eliminazione');
      }
    } catch (error: any) {
      console.error('Unexpected error in deletion:', error);
      toast.error(error.message || 'Si è verificato un errore imprevisto');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={setIsOpen}>
      <AlertDialogTrigger asChild>
        <Button
          variant="outline"
          className="w-full border-red-500/20 text-red-400 hover:bg-red-500/10 hover:border-red-500/40"
        >
          <Trash2 className="w-4 h-4 mr-2" />
          Elimina Account
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent className="glass border-red-500/20 max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-white flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-400" />
            Elimina Account Definitivamente
          </AlertDialogTitle>
          <AlertDialogDescription className="text-muted-foreground space-y-3">
            <p>
              <strong className="text-red-400">Attenzione:</strong> Questa azione è irreversibile e comporterà:
            </p>
            <ul className="list-disc list-inside space-y-1 text-sm">
              <li>Eliminazione permanente del tuo profilo</li>
              <li>Rimozione di tutti i tuoi post e commenti</li>
              <li>Cancellazione dei tuoi pet dal database</li>
              <li>Eliminazione di tutte le conversazioni</li>
              <li>Perdita di tutti i dati dell'account</li>
            </ul>
            <p className="text-red-400 font-medium">
              Non sarà possibile recuperare nessun dato dopo l'eliminazione.
            </p>
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="confirm" className="text-white">
              Per confermare, scrivi <span className="font-mono font-bold">ELIMINA</span>
            </Label>
            <Input
              id="confirm"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={t('account.confirmDelete')}
              className="font-mono"
              autoComplete="off"
            />
          </div>

          {user?.email && (
            <div className="p-3 bg-destructive/10 rounded-lg border border-destructive/20">
              <p className="text-sm text-muted-foreground">
                Account da eliminare: <span className="text-white">{user.email}</span>
              </p>
            </div>
          )}
        </div>

        <AlertDialogFooter className="flex-col sm:flex-row gap-2">
          <AlertDialogCancel 
            className="glass border-white/20 text-white hover:bg-white/10"
            onClick={() => {
              setConfirmText('');
              setIsOpen(false);
            }}
          >
            Annulla
          </AlertDialogCancel>
          <AlertDialogAction
            className="bg-red-500 hover:bg-red-600 text-white border-0"
            onClick={handleDeleteAccount}
            disabled={loading || confirmText.trim().toUpperCase() !== 'ELIMINA'}
          >
            {loading ? (
              <>
                <div className="w-4 h-4 mr-2 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                Eliminazione...
              </>
            ) : (
              <>
                <Trash2 className="w-4 h-4 mr-2" />
                Elimina Account
              </>
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default DeleteAccountButton;